import { useState, useMemo } from 'react'
import {Search, SlidersHorizontal, ChevronDown, Shield, ShieldCheck, ShieldOff, Clock, Loader2} from 'lucide-react'
import Layout from '@/components/Layout'
import ProjectCard from '@/components/ProjectCard'
import { CHAIN_LABELS } from '@/lib/types'
import type { EvmChain } from '@/lib/types'
import { useAllProjects } from '@/hooks/useProjects'

const C = {
  bgDeep:     '#000000',
  surface:    '#070a0c',
  bgCard:     '#070a0c',
  turq:       '#1ad6c4',
  turqDim:    'rgba(26,214,196,0.12)',
  borderTurq: 'rgba(26,214,196,0.30)',
  border:     'rgba(255,255,255,0.08)',
  borderMed:  'rgba(255,255,255,0.14)',
  textSecond: '#b4c0cc',
  textMuted:  '#6a7682',
}

type StatusFilter = 'all' | 'completed' | 'in_review' | 'pending'
type SortOption   = 'newest' | 'score_high' | 'score_low' | 'name'

const STATUS_TABS: { key: StatusFilter; label: string; icon: React.ElementType }[] = [
  { key: 'all',       label: 'All Projects', icon: Shield },
  { key: 'completed', label: 'Audited',       icon: ShieldCheck },
  { key: 'in_review', label: 'In Review',     icon: Clock },
  { key: 'pending',   label: 'Unaudited',     icon: ShieldOff },
]

const SORT_OPTIONS: { key: SortOption; label: string }[] = [
  { key: 'newest',     label: 'Newest First' },
  { key: 'score_high', label: 'Score: High → Low' },
  { key: 'score_low',  label: 'Score: Low → High' },
  { key: 'name',       label: 'Name A → Z' },
]

const inputStyle = {
  background: '#05080a',
  border: `1px solid ${C.border}`,
  color: '#ffffff',
}

export default function Projects() {
  const { projects, loading } = useAllProjects()
  const [query,   setQuery]   = useState('')
  const [chain,   setChain]   = useState<EvmChain | 'all'>('all')
  const [status,  setStatus]  = useState<StatusFilter>('all')
  const [sort,    setSort]    = useState<SortOption>('newest')
  const [showSort, setShowSort] = useState(false)

  const filtered = useMemo(() => {
    let list = [...projects]

    // search
    if (query.trim()) {
      const q = query.toLowerCase()
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.symbol.toLowerCase().includes(q) ||
          p.contractAddress.toLowerCase().includes(q),
      )
    }
    // chain
    if (chain !== 'all') list = list.filter((p) => p.chain === chain)

    // status
    if (status === 'completed') list = list.filter((p) => p.audit?.status === 'completed')
    else if (status === 'in_review') list = list.filter((p) => p.audit?.status === 'in_review')
    else if (status === 'pending') list = list.filter((p) => !p.audit || p.audit.status === 'pending')

    // sort
    if (sort === 'score_high') list.sort((a, b) => (b.audit?.score ?? -1) - (a.audit?.score ?? -1))
    else if (sort === 'score_low') list.sort((a, b) => (a.audit?.score ?? 999) - (b.audit?.score ?? 999))
    else if (sort === 'name') list.sort((a, b) => a.name.localeCompare(b.name))
    else list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    return list
  }, [projects, query, chain, status, sort])

  // Per-status counts
  const counts = useMemo(() => ({
    all:       projects.length,
    completed: projects.filter((p) => p.audit?.status === 'completed').length,
    in_review: projects.filter((p) => p.audit?.status === 'in_review').length,
    pending:   projects.filter((p) => !p.audit || p.audit.status === 'pending').length,
  }), [projects])

  const currentSort = SORT_OPTIONS.find((s) => s.key === sort)!

  return (
    <Layout>
      {/* Header */}
      <section style={{ background: C.surface, borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-[1100px] mx-auto px-5 py-14">
          <span
            className="text-xs font-semibold tracking-widest uppercase mb-3 inline-block"
            style={{ color: C.turq, letterSpacing: '0.12em' }}
          >
            Audit Registry
          </span>
          <h1
            className="text-white font-light leading-tight"
            style={{ fontSize: 'clamp(26px, 4vw, 40px)', letterSpacing: '-0.8px' }}
          >
            Audited Projects
          </h1>
          <p className="mt-3 text-sm max-w-xl" style={{ color: C.textMuted }}>
            Browse smart contracts audited by Crypto Audit Service. Each listing shows the audit verdict, score, and full vulnerability report.
          </p>
        </div>
      </section>

      <section style={{ background: C.bgDeep }} className="py-10">
        <div className="max-w-[1100px] mx-auto px-5">

          {/* Search + Sort row */}
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            {/* Search */}
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: C.textMuted }}
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, symbol, or contract address…"
                className="w-full rounded-md pl-9 pr-4 py-2.5 text-sm outline-none"
                style={inputStyle}
              />
            </div>

            {/* Chain filter */}
            <select
              value={chain}
              onChange={(e) => setChain(e.target.value as EvmChain | 'all')}
              className="rounded-md px-3.5 py-2.5 text-sm outline-none cursor-pointer sm:w-44"
              style={inputStyle}
            >
              <option value="all">All Chains</option>
              {Object.entries(CHAIN_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>

            {/* Sort dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowSort((v) => !v)}
                className="inline-flex items-center gap-2 rounded-md px-3.5 py-2.5 text-sm transition-all hover:bg-white/5 w-full sm:w-auto"
                style={{ border: `1px solid ${C.borderMed}`, color: C.textSecond }}
              >
                <SlidersHorizontal className="w-4 h-4" />
                {currentSort.label}
                <ChevronDown className="w-3.5 h-3.5 ml-auto" style={{ opacity: 0.5 }} />
              </button>
              {showSort && (
                <div
                  className="absolute right-0 top-full mt-1 rounded-xl overflow-hidden z-20 min-w-[180px] py-1"
                  style={{ background: '#0a0e12', border: `1px solid ${C.border}`, boxShadow: '0 8px 40px rgba(0,0,0,0.8)' }}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => { setSort(opt.key); setShowSort(false) }}
                      className="w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-white/5"
                      style={{ color: sort === opt.key ? C.turq : C.textSecond }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Status tabs */}
          <div
            className="flex gap-1 p-1 rounded-xl mb-8 overflow-x-auto"
            style={{ background: C.surface, border: `1px solid ${C.border}` }}
          >
            {STATUS_TABS.map(({ key, label, icon: Icon }) => {
              const active = status === key
              return (
                <button
                  key={key}
                  onClick={() => setStatus(key)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm whitespace-nowrap flex-1 justify-center transition-all"
                  style={{
                    background: active ? C.turqDim : 'transparent',
                    color: active ? '#ffffff' : C.textMuted,
                    border: active ? `1px solid ${C.borderTurq}` : '1px solid transparent',
                    fontWeight: active ? 500 : 400,
                  }}
                >
                  <Icon className="w-3.5 h-3.5" style={{ color: active ? C.turq : 'inherit' }} />
                  {label}
                  <span
                    className="text-xs px-1.5 py-0.5 rounded-full font-mono"
                    style={{
                      background: active ? C.turq : 'rgba(255,255,255,0.07)',
                      color: active ? '#000' : C.textMuted,
                    }}
                  >
                    {counts[key]}
                  </span>
                </button>
              )
            })}
          </div>

          {/* Results count */}
          <p className="text-xs mb-5" style={{ color: C.textMuted }}>
            Showing <span className="text-white font-medium">{filtered.length}</span> project{filtered.length !== 1 ? 's' : ''}
            {query && <> matching "<span style={{ color: C.turq }}>{query}</span>"</>}
          </p>

          {/* Grid */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 className="w-7 h-7 animate-spin" style={{ color: C.turq }} />
              <p className="text-sm" style={{ color: C.textMuted }}>Loading projects…</p>
            </div>
          ) : filtered.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((p) => (
                <ProjectCard key={p._id} project={p} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}` }}
              >
                <Search className="w-6 h-6" style={{ color: C.turq }} />
              </div>
              <p className="text-white font-medium">No projects found</p>
              <p className="text-sm text-center max-w-xs" style={{ color: C.textMuted }}>
                Try adjusting your search term, chain filter, or status tab.
              </p>
              <button
                onClick={() => { setQuery(''); setChain('all'); setStatus('all') }}
                className="px-4 py-2 rounded-md text-sm font-medium transition-all hover:brightness-110"
                style={{ background: C.turq, color: '#000' }}
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </section>
    </Layout>
  )
}
